const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.prototype.toString.call(value) === '[object Object]'
  );
};

const encodeToBase64 = (str: string): string => {
  try {
    // Use TextEncoder for proper UTF-8 encoding to handle Unicode
    const utf8Encoder = new TextEncoder();
    const utf8Bytes = utf8Encoder.encode(str);

    // Convert bytes to binary string
    const binaryString = String.fromCharCode(...utf8Bytes);

    // Encode to Base64
    return window.btoa(binaryString);
  } catch (error) {
    console.error('Base64 encoding failed:', error);
    throw new Error('Failed to encode string to Base64');
  }
};

export const encodeObjectToBase64 = <T extends Record<string, unknown>>(obj: T): T => {
  const result = {} as T;

  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) {
      result[key as keyof T] = value as T[keyof T];
    } else if (Array.isArray(value)) {
      // Handle arrays - encode string items, recurse into objects
      result[key as keyof T] = value.map((item) => {
        if (typeof item === 'string') {
          return encodeToBase64(item);
        } else if (isPlainObject(item)) {
          return encodeObjectToBase64(item);
        }
        return item;
      }) as T[keyof T];
    } else if (isPlainObject(value)) {
      result[key as keyof T] = encodeObjectToBase64(value) as T[keyof T];
    } else if (typeof value === 'string') {
      result[key as keyof T] = encodeToBase64(value) as T[keyof T];
    } else {
      result[key as keyof T] = value as T[keyof T];
    }
  }

  return result;
};

export const withBase64Encoding = async <TData extends Record<string, unknown>, TResponse>(
  apiFunction: (data: TData) => Promise<TResponse>,
  data: TData
): Promise<TResponse> => {
  const encodedData = encodeObjectToBase64(data);
  return apiFunction(encodedData);
};
